import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { OrdersDrilldownCustomersResponseDto } from '@/generated/korza-api-client/types.gen';
import { getLuxonLocale } from '@/lib/utils';
import { CUSTOMER_SORTS, type CustomerSort } from '@/lib/orders-drilldown';
import { formatCurrency } from '@/components/metrics/formatters';
import { OrdersDrilldownCampaignCell } from '@/components/orders-drilldown/orders-drilldown-campaign-cell';
import {
  OrdersDrilldownSortHeader,
  getNextSort,
  getSortDirection,
} from '@/components/orders-drilldown/orders-drilldown-sort-header';
import {
  MotionTableRow,
  getRowEntrance,
} from '@/components/orders-drilldown/motion-table-row';
import { CustomerAvatar } from '@/components/orders-drilldown/customer-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type CustomerRow = OrdersDrilldownCustomersResponseDto['items'][number];

const SKELETON_ROWS = 10;
const COLUMN_COUNT = 6;

function hasText(value: string | null | undefined): value is string {
  return Boolean(value?.trim());
}

interface OrdersDrilldownCustomersTableProps {
  customers: CustomerRow[];
  isLoading: boolean;
  /** Showing the previous page while the next one loads. */
  isRefreshing: boolean;
  /** Active server-side sort (total spent or order count). */
  sort: CustomerSort;
  onSortChange: (sort: CustomerSort) => void;
}

export function OrdersDrilldownCustomersTable({
  customers,
  isLoading,
  isRefreshing,
  sort,
  onSortChange,
}: OrdersDrilldownCustomersTableProps) {
  const { LL, locale } = useI18nContext();

  const formatDate = (iso: string) =>
    DateTime.fromISO(iso)
      .setLocale(getLuxonLocale(locale))
      .toLocaleString(DateTime.DATE_MED);

  const sortBy = (field: 'order_count' | 'total_spent') => () => {
    const next = getNextSort(field, sort, CUSTOMER_SORTS);
    if (next) onSortChange(next);
  };

  return (
    <Table containerClassName="overflow-visible">
      <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_hsl(var(--border))]">
        <TableRow>
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.customerColumns.customer()}
          </TableHead>
          <OrdersDrilldownSortHeader
            alignRight
            label={LL.metricsPage.repurchaseDrilldown.customerColumns.orderCount()}
            direction={getSortDirection('order_count', sort)}
            onSort={sortBy('order_count')}
          />
          <OrdersDrilldownSortHeader
            alignRight
            label={LL.metricsPage.repurchaseDrilldown.customerColumns.totalSpent()}
            direction={getSortDirection('total_spent', sort)}
            onSort={sortBy('total_spent')}
          />
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.customerColumns.firstOrder()}
          </TableHead>
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.customerColumns.lastOrder()}
          </TableHead>
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.customerColumns.campaign()}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody
        className={`transition-opacity duration-200 ${isRefreshing ? 'opacity-50' : ''}`}
      >
        {isLoading
          ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {Array.from({ length: COLUMN_COUNT }).map((__, cell) => (
                  <TableCell key={cell}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : customers.map((customer, index) => {
              const isIdentified =
                hasText(customer.name) ||
                hasText(customer.email) ||
                hasText(customer.phone);
              const secondary = hasText(customer.email)
                ? customer.email
                : hasText(customer.phone)
                  ? customer.phone
                  : null;
              return (
                <MotionTableRow key={customer.key} {...getRowEntrance(index)}>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2.5">
                      {isIdentified ? (
                        <CustomerAvatar
                          name={customer.name ?? secondary}
                          seed={secondary}
                        />
                      ) : (
                        <CustomerAvatar name={null} seed={null} />
                      )}
                      <div className="min-w-0">
                        {isIdentified ? (
                          <>
                            <div>
                              {hasText(customer.name)
                                ? customer.name
                                : (secondary ?? '—')}
                            </div>
                            {hasText(customer.name) && secondary !== null ? (
                              <div className="text-xs text-muted-foreground">
                                {secondary}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            {LL.metricsPage.repurchaseDrilldown.unidentifiedCustomer()}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    {customer.orderCount}
                  </TableCell>
                  <TableCell className="py-2 whitespace-nowrap text-right">
                    {formatCurrency(
                      customer.totalSpent,
                      locale,
                      customer.currency
                    )}
                  </TableCell>
                  <TableCell className="py-2 whitespace-nowrap text-muted-foreground">
                    {formatDate(customer.firstOrderAt)}
                  </TableCell>
                  <TableCell className="py-2 whitespace-nowrap text-muted-foreground">
                    {formatDate(customer.lastOrderAt)}
                  </TableCell>
                  <TableCell className="py-2">
                    <OrdersDrilldownCampaignCell
                      campaign={customer.lastCampaign}
                    />
                  </TableCell>
                </MotionTableRow>
              );
            })}
      </TableBody>
    </Table>
  );
}
